import { useTranslation } from "react-i18next";
import { changeLanguage as changeAppLanguage } from "@/i18n";
import { Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const LANGS = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
] as const;

export function LanguageSwitcher({
  className,
  variant = "ghost",
  onChange,
}: {
  className?: string;
  variant?: "ghost" | "outline";
  onChange?: (lang: string) => void;
}) {
  const { i18n } = useTranslation();
  const current = (i18n.language || "en").startsWith("ar") ? "ar" : "en";
  const currentLabel = LANGS.find((l) => l.code === current)?.label ?? "English";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition rtl:tracking-normal",
          variant === "outline"
            ? "border border-input bg-background hover:bg-accent"
            : "hover:bg-accent",
          className,
        )}
        aria-label="Change language"
      >
        <Languages className="h-4 w-4" />
        <span>{currentLabel}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => {
              const y = window.scrollY;
              void changeAppLanguage(l.code).then(() => {
                requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: "auto" }));
                onChange?.(l.code);
              });
            }}
            className={cn(current === l.code && "font-semibold")}
          >
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
